import React, { useState, useRef, useEffect, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, SectionList, ScrollView, Dimensions } from 'react-native';
import debounce from 'lodash.debounce';
import { styles } from './components/AppStyles';
import BottomNavBar from './BottomNavBar'; // Adjust the path if necessary
import screenGroups from './real-data.json';

/**
 * SearchScreen Component
 * 
 * Allows the user to search through a list of terms and their definitions.
 * The terms are displayed in sections based on the first letter of the term.
 * The user can either:
 * - Search by term or definition using the search bar.
 * - Quickly navigate to terms starting with a particular letter using the side navigation.
 */

const SearchScreen = ({ navigation }) => {
  // Reference to the SectionList for programmatically scrolling to sections.
  const sectionListRef = useRef(null);

  // States
  const [isRendered, setIsRendered] = useState(false); // Determines if SectionList has been rendered.
  const [searchTerm, setSearchTerm] = useState(''); // The current search term entered by the user.
  const [filteredData, setFilteredData] = useState([]); // Data filtered by search term.
  const [letters, setLetters] = useState([]); // Alphabet letters for navigation.
  const [filterByLetter, setFilterByLetter] = useState(null); // Current letter being used to filter the list.

// Effect to remove the home button in the top left corner
React.useLayoutEffect(() => {
    navigation.setOptions({
        headerLeft: null, // Setting headerLeft to null removes the button
    });
    }, [navigation]);

  // Compute a sorted list of all the screens from the data.
  const sortedData = useMemo(() => {
      return screenGroups.flatMap(group => group.screens).sort((a, b) => a.title.localeCompare(b.title));
  }, [screenGroups]);

  // Effect to auto-scroll the SectionList to the top when a letter is selected.
  useEffect(() => {
      setTimeout(() => {
          sectionListRef.current?.scrollToLocation({
              sectionIndex: 0,
              itemIndex: 0,
              animated: true,
          });
      }, 200);
  }, [filterByLetter]);  

  // Search functionality: filters data based on search term or definition.
  const handleSearch = (text) => {
      setSearchTerm(text);
      setFilterByLetter(null); // Reset any letter filter when text is entered.
      debouncedSearch(text);
  };

  // Scroll to the section of data items starting with a specific letter.
  const handleLetterPress = (letter) => {
      setFilterByLetter(letter);
      setSearchTerm(''); // Clear the search bar when a letter is selected.
  };

  // Reset the letter filter to show all data items.
  const handleResetLetterFilter = () => {
      setFilterByLetter(null);
  };

  // Compute the sections of data for rendering based on filters.
  const sectionedData = useMemo(() => {
      const dataToGroup = filterByLetter 
          ? sortedData.filter(item => item.title[0].toUpperCase() === filterByLetter)
          : (filteredData.length === 0 ? sortedData : filteredData);

      // Group data by the first letter of the title.
      const result = dataToGroup.reduce((acc, item) => {
          const firstLetter = item.title[0].toUpperCase();
          if (!acc[firstLetter]) {
              acc[firstLetter] = { title: firstLetter, data: [] };
          }
          acc[firstLetter].data.push(item);
          return acc;
      }, {});
      
      // Set the letters that have associated data.
      setLetters(Object.keys(result));

      return result;
  }, [filteredData, sortedData, filterByLetter]);

  // Debounced search function to reduce the number of calls during user typing.
  const debouncedSearch = debounce((text) => {
      const lowercasedSearchTerm = text.toLowerCase();
      const filtered = sortedData.filter(item => 
          item.title.toLowerCase().includes(lowercasedSearchTerm) ||
          item.definition.toLowerCase().includes(lowercasedSearchTerm)
      );
      setFilteredData(filtered);
  }, 300);

  // Utility function to highlight the search term within text.
  const highlightText = (text) => {
      const startIndex = text.toLowerCase().indexOf(searchTerm.toLowerCase());
      if (startIndex === -1) return <Text>{text}</Text>;

      const endIndex = startIndex + searchTerm.length;
      return (
          <Text>
              {text.substring(0, startIndex)}
              <Text style={styles.highlighted}>{text.substring(startIndex, endIndex)}</Text>
              {text.substring(endIndex)}
          </Text>
      );
  };

  // Get the screen height
  const screenHeight = Dimensions.get('window').height;

  // Calculate dynamic padding. Assuming the height of each letter button is 40,
  // you may want to adjust the number 40 if the buttons are of different height.
  const dynamicPadding = screenHeight / 2 - 40 / 2;
  
  useEffect(() => {
    // Assuming sortedData is memoized and does not change on every render
    if (isRendered && sortedData.length > 0) {
      const timer = setTimeout(() => {
        sectionListRef.current?.scrollToLocation({
          sectionIndex: 0,
          itemIndex: 0,
          viewPosition: 0,
          animated: false,
        });
      }, 0); // Adjust delay if necessary
      
      return () => clearTimeout(timer);
    }
  }, [isRendered, sortedData]);  
  
  return (
    <View style={styles.container}>
        <View style={styles.searchContainer}>
            {/* Text Input for Searching */}
            <TextInput
                value={searchTerm}
                onChangeText={handleSearch}
                placeholder="Search by term or definition..."
                placeholderTextColor="#999"
                style={styles.textInput}
            />
        </View>

        {/* Container for the Main List */}
        <View style={styles.scrollContainer}>

            {/* Section List displaying terms grouped by their starting letter */}
            <SectionList
                ref={sectionListRef}
                onLayout={() => setIsRendered(true)} // Track when the list is rendered.
                sections={Object.values(sectionedData)} // Data sections.
                keyExtractor={(item, index) => item.title + index} // Unique key for each list item.

                // Render each data item.
                renderItem={({ item, index }) => (
                    <View 
                        key={`${item.title}-${index}`} 
                        style={[styles.item, index % 2 === 0 && styles.itemEven]}
                    >
                        <Text style={styles.itemTerm}>{highlightText(item.title)}</Text>{/* Highlighted term. */}
                        <Text style={styles.itemDefinition}>{highlightText(item.definition)}</Text>{/* Highlighted definition. */}
                    </View>
                )}

                // Render the header for each section (i.e., the letter).
                renderSectionHeader={({ section: { title } }) => (
                    <Text style={styles.groupLetter}>
                        {title}
                    </Text>
                )}
            />
        </View>

        {/* Container for Letter Navigation */}
        <View style={styles.lettersContainer}>
            <ScrollView 
                horizontal={false}
                vertical={true}
                bounces={false}
                alwaysBounceVertical={false} // Prevents vertical bounce specifically
                showsVerticalScrollIndicator={false} // This will hide the vertical scrollbar
                contentContainerStyle={{
                    flexGrow: 0,
                    justifyContent: 'center',
                    paddingBottom: dynamicPadding, // Dynamic bottom padding                    
                }}
            >
                {/* Conditional rendering based on if a letter is selected for filtering */}
                {filterByLetter ? (
                    // If a letter is filtered, display that letter and a reset button.
                    <>
                        <TouchableOpacity
                            onPress={() => handleLetterPress(filterByLetter)}
                            style={styles.letter}
                        >
                            <Text style={styles.letterText}>{filterByLetter}</Text>{/* The selected letter. */}
                        </TouchableOpacity>
                        <TouchableOpacity
                            onPress={handleResetLetterFilter}
                            style={styles.letter}
                        >
                            <Text style={styles.letterText}>&#8634;</Text>{/* Reset button to clear the letter filter. */}
                        </TouchableOpacity>
                    </>
                ) : (
                    // If not filtering by a specific letter, display all available letters.
                    letters.map((letter) => (
                        <TouchableOpacity
                            key={letter}
                            onPress={() => handleLetterPress(letter)}
                            style={styles.letter}
                        >
                            <Text style={styles.letterText}>{letter}</Text>{/* Individual letter. */}
                        </TouchableOpacity>
                    ))
                )}
            </ScrollView>
        </View>

        {/* Bottom Navigation Bar */}
        <BottomNavBar navigation={navigation} current="Search" />
    </View>
  );
};

export default SearchScreen;